import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./ProfilePage.css";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { FaPencilAlt, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaVenusMars, FaTrash, FaSave, FaTimes, FaCheckCircle, FaBriefcase } from "react-icons/fa";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import Autocomplete from "../AutoComplete";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import VerticalAd from "../VerticalBanner";
import { apiUrl, jsonAuthHeaders, otpApiUrl, isOtpApiConfigured } from "../../config/api.js";
import { devLog, devWarn } from "../../config/devLog.js";
import { setUserDetails } from "../../redux/userSlice.js";

function normalizeProfileEmail(value) {
  return String(value ?? "").trim().toLowerCase();
}

/** Same as signup: accepts `company.com`, `@company.com`, or full email → domain only */
function normalizeOrgEmailDomainInput(raw) {
  let s = String(raw ?? "").trim().toLowerCase();
  if (!s) return "";
  s = s.replace(/^@+/, "");
  const at = s.lastIndexOf("@");
  if (at >= 0) s = s.slice(at + 1).trim();
  return s.replace(/^@+/, "");
}

/** Join address parts with commas only when present — avoids ", , ," when nothing is saved */
function formatAddressDisplay(address = {}) {
  const stateZip = [address.state, address.pincode].filter(Boolean).join(" ").trim();
  const segments = [address.street, address.city, stateZip, address.country]
    .map((s) => (s == null ? "" : String(s).trim()))
    .filter(Boolean);
  return segments.join(", ");
}

const ProfileField = ({ label, value, onChange, isEditing, icon, fieldType = "text", isGlobalEditMode = false, isEditable = true }) => (
  <div className={`profile-field ${isEditing || (isGlobalEditMode && isEditable) ? 'editing' : ''}`}>
    <label>
      {icon && <span className="field-icon">{icon}</span>}
      {label}
    </label>
    {isEditing || (isGlobalEditMode && isEditable) ? (
      fieldType === "mobile" ? (
        <PhoneInput
          defaultCountry="us"
          value={value}
          onChange={(phone) => onChange({ target: { value: phone } })}
          className="custom-phone-input"
          preferredCountries={["us"]}
        />
      ) : fieldType === "date" ? (
        <input type="date" value={value} onChange={onChange} />
      ) : fieldType === "textarea" ? (
        <textarea 
          value={value} 
          onChange={onChange} 
          rows="3"
          placeholder={`Enter ${label.toLowerCase()}...`}
        />
      ) : (
        <input 
          type={fieldType} 
          value={value} 
          onChange={onChange}
          placeholder={`Enter ${label.toLowerCase()}...`}
        />
      )
    ) : (
      <p>{value || 'Not provided'}</p>
    )}
  </div>
);

const AddressField = ({ address, isEditing, onChange, isGlobalEditMode = false }) => (
  <div className={`profile-field ${isEditing || isGlobalEditMode ? 'editing' : ''}`}>
    <label>
      <FaMapMarkerAlt className="field-icon" />
      Full Address
    </label>
    {isEditing || isGlobalEditMode ? (
      <div className="address-edit-group">
        {['street', 'city', 'state', 'pincode', 'country'].map((key) => (
          <input
            key={key}
            type="text"
            placeholder={key.charAt(0).toUpperCase() + key.slice(1)}
            value={address[key] || ''}
            onChange={(e) => onChange(key, e.target.value)}
          />
        ))}
      </div>
    ) : (
      <p>{formatAddressDisplay(address) || ""}</p>
    )}
  </div>
);

const ProfilePage = () => {
    const dispatch = useDispatch();
    const { userId } = useSelector((state) => state.userReducer);
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [savedCorporateEmail, setSavedCorporateEmail] = useState("");
    const [corpOtp, setCorpOtp] = useState("");
    const [corpOtpBusy, setCorpOtpBusy] = useState(false);
    const [corpLocalPart, setCorpLocalPart] = useState("");
    const [companies, setCompanies] = useState({});
    const [sponsoredAds, setSponsoredAds] = useState([]);
    const [activeSponsoredIndex, setActiveSponsoredIndex] = useState(0);

  const [profile, setProfile] = useState({
    aboutMe: "Experienced Business Consultant with a passion for strategic growth and innovation.",
    firstName: "Raj",
    lastName: "Verma",
    gender: "Male",
    dob: "1985-03-10",
    email: "raj@gmail.com",
    corporateEmail: "",
    mobile: "+16565804452",
    profilePic: "",
    address: {
      street: "789 Downtown SF",
      city: "New York",
      state: "NY",
      pincode: "631201",
      country: "USA"
    },
    preferredLocation: "San Francisco",
    companyName: "",
    isCorporateVerified: false,
  });
  const [updatedPic, setUpdatedPic] = useState("");
  const [editingField, setEditingField] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

    const getProfileData = useCallback(async () => {
      try {
        devLog("Fetching profile data for userId:", userId)

        if (!userId) {
          devWarn("No userId available, using default profile data")
          setIsLoading(false)
          return
        }

        const response = await fetch(apiUrl("getUser?" + userId))

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const profileData = await response.json()
        devLog("API Response:", profileData)

        const preferredLocationParts = profileData.preferred_location ? profileData.preferred_location.split("_") : ["", ""]
        const [, preferredstate] = preferredLocationParts

        const addressParts = profileData.address ? profileData.address.split("_") : ["", "", "", "", ""]
        const [street, city, state, pincode, country] = addressParts

        const updatedProfile = {
          firstName: profileData.first_name || "",
          lastName: profileData.last_name || "",
          gender: profileData.gender || "",
          dob: profileData.date_of_birth ? moment(profileData.date_of_birth).format("YYYY-MM-DD") : "",
          email: profileData.email || "",
          corporateEmail: profileData.corporate_email || "",
          mobile: profileData.mobile_number || "",
          isEmailVerified: true,
          isCorporateVerified:
            profileData.is_verified === true ||
            profileData.is_verified === 1 ||
            profileData.is_verified === "1",
          aboutMe: profileData.about_me || "",
          profilePic: profileData.profile_pic || "",
          companyName: profileData.companyName || profileData.company_name || "",
          address: {
            street: street || "",
            city: city || "",
            state: state || "",
            pincode: pincode || "",
            country: country || "",
          },
          preferredLocation: preferredstate || ""
        }

        devLog("Updated profile:", updatedProfile)
        setProfile(updatedProfile)
        setSavedCorporateEmail(normalizeProfileEmail(profileData.corporate_email || ""))
        const ce = profileData.corporate_email || "";
        const atIdx = ce.lastIndexOf("@");
        setCorpLocalPart(atIdx > 0 ? ce.slice(0, atIdx) : "")

      } catch (error) {
        devLog("Error fetching profile data:", error?.message)
        toast.error("Failed to load profile data. Using default values.")
      } finally {
        setIsLoading(false)
      }
    }, [userId])

  const corporateEmailChanged = useMemo(
    () =>
      normalizeProfileEmail(profile.corporateEmail) !==
      normalizeProfileEmail(savedCorporateEmail),
    [profile.corporateEmail, savedCorporateEmail]
  );

  const resolvedCompanyDomain = useMemo(() => {
    const name = (profile.companyName || "").trim();
    if (!name || !companies || typeof companies !== "object") return "";
    const key = Object.keys(companies).find(
      (k) => String(k || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (!key) return "";
    return normalizeOrgEmailDomainInput(companies[key]);
  }, [profile.companyName, companies]);

  const emailTailDomain = useMemo(() => {
    const ce = profile.corporateEmail || "";
    const at = ce.lastIndexOf("@");
    if (at <= 0) return "";
    return normalizeOrgEmailDomainInput(ce.slice(at + 1));
  }, [profile.corporateEmail]);

  const effectiveDomain = useMemo(() => {
    if (resolvedCompanyDomain) {
      if (!emailTailDomain || emailTailDomain === resolvedCompanyDomain) {
        return resolvedCompanyDomain;
      }
      return emailTailDomain;
    }
    return emailTailDomain;
  }, [resolvedCompanyDomain, emailTailDomain]);

  const showCorpSplit = Boolean(effectiveDomain);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl("getCompaniesData"));
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data && typeof data === "object") setCompanies(data);
      } catch (_) {
        /* optional directory */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!effectiveDomain) return;
    const local = String(corpLocalPart || "").trim().toLowerCase();
    const full = local ? `${local}@${effectiveDomain}` : "";
    setProfile((p) => (p.corporateEmail === full ? p : { ...p, corporateEmail: full }));
  }, [corpLocalPart, effectiveDomain]);

  const handleUpdate = async () => {
    devLog("profile", profile);
    devLog("profile", updatedPic);

    //  if (validateFields()) {
    //   setEditFields({});
      const payload = {
          id: userId,
          "about_me":profile.aboutMe,
          email: profile.email,
          mobile_number: profile.mobile,
          address: Object.values(profile.address).join("_"),
          preferred_location: "USA_"+profile.preferredLocation,
          isProfilePicChanged: updatedPic.length>0?true:false,
          profilePic: updatedPic.length>0?{base64:updatedPic, fileName:"test123"}:profile.profilePic,
          imageUrl: updatedPic.length>0?updatedPic:profile.profilePic
      };
      if (!corporateEmailChanged) {
        payload.corporate_email = profile.corporateEmail;
      } else if (normalizeProfileEmail(profile.corporateEmail) === "") {
        payload.corporate_email = "";
      }

      const updatedResponse = await fetch(apiUrl("updateProfile"),{
        method:"POST",
        headers: jsonAuthHeaders(),
        body:JSON.stringify(payload)
      })
      const res = await updatedResponse.json()
      devLog(res)
      if (!updatedResponse.ok) {
        toast.error(res.message || "Could not update profile.");
        throw new Error(res.message || "update failed");
      }
      if (payload.corporate_email === "") {
        setSavedCorporateEmail("");
        setProfile((p) => ({ ...p, isCorporateVerified: false }));
      }
      setEditingField(null)
    // }
  };

  const handleDelete = async () => {
    if (
      userId == null ||
      userId === "" ||
      String(userId) === "null" ||
      String(userId) === "undefined"
    ) {
      toast.error("Not signed in.");
      return;
    }
    try {
      const updatedResponse = await fetch(apiUrl("deleteAccount"), {
        method: "POST",
        headers: jsonAuthHeaders(),
        body: JSON.stringify({ id: userId }),
      });
      const res = await updatedResponse.json();
      setIsOpen(false);
      if (!updatedResponse.ok) {
        toast.error(res.message || "Could not delete account.");
        return;
      }
      toast.success("User Account Deleted Successfully");
      localStorage.clear();
      navigate("/home");
      devLog(res);
    } catch (err) {
      devWarn("deleteAccount", err?.message);
      toast.error("Could not delete account.");
      setIsOpen(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile({ ...profile, [field]: value });
  };

  const handleAddressChange = (key, value) => {
    setProfile((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [key]: value,
      },
    }));
  };


  const handleUpdateAllData = async () => {
    try {
      await handleUpdate();
      setIsEditingProfile(false);
      setEditingField(null);
      if (corporateEmailChanged) {
        if (normalizeProfileEmail(profile.corporateEmail) === "") {
          toast.success("Profile updated. Corporate email was removed.");
        } else {
          toast.info(
            "Your profile was saved. Complete corporate email verification below to save the new address.",
            { autoClose: 5000 }
          );
        }
      } else {
        toast.success("Profile updated successfully!");
      }
    } catch (error) {
      toast.error("Failed to update profile. Please try again.");
    }
  };

  const handleSendCorporateOtp = async () => {
    const email = normalizeProfileEmail(profile.corporateEmail);
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid corporate email address first.");
      return;
    }
    if (!isOtpApiConfigured()) {
      toast.error("OTP service is not configured. Contact support.");
      return;
    }
    setCorpOtpBusy(true);
    try {
      const response = await fetch(otpApiUrl("getOTP"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error("Could not send verification code. Try again.");
        return;
      }
      setCorpOtp("");
      toast.success("Verification code sent to your corporate email.");
    } catch (e) {
      devWarn("getOTP corporate", e?.message);
      toast.error("Could not send verification code.");
    } finally {
      setCorpOtpBusy(false);
    }
  };

  const handleVerifyCorporateOtp = async () => {
    const email = normalizeProfileEmail(profile.corporateEmail);
    const otp = String(corpOtp ?? "").trim();
    if (!email || !otp) {
      toast.error("Enter the code from your email.");
      return;
    }
    setCorpOtpBusy(true);
    try {
      const response = await fetch(apiUrl("verifyOTP"), {
        method: "POST",
        headers: jsonAuthHeaders(),
        body: JSON.stringify({ email, otp, id: userId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.message || "Verification failed.");
        return;
      }
      toast.success(data.message || "Corporate email verified.");
      setCorpOtp("");
      await getProfileData();
      const userResponse = await fetch(apiUrl("getUser?" + userId));
      if (userResponse.ok) {
        const profileData = await userResponse.json();
        dispatch(
          setUserDetails({
            ...profileData,
            userId: profileData.id,
            name: `${profileData.first_name || ""} ${profileData.last_name || ""}`.trim(),
            userEmail: profileData.email,
            company: profileData.companyName ? profileData.companyName : "",
            isVerified: profileData.is_verified,
            corporateEmail: profileData.corporate_email,
          })
        );
      }
    } catch (e) {
      devWarn("verifyOTP profile", e?.message);
      toast.error("Verification failed.");
    } finally {
      setCorpOtpBusy(false);
    }
  };

  const handleEditProfile = () => {
    setIsEditingProfile(true);
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    setEditingField(null);
    // Optionally reload profile data to discard changes
    getProfileData();
  };

const handleImageUpload = (e) => {
  const file = e.target.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = () => {
      // Remove the "data:image/...;base64," prefix
      setUpdatedPic(reader.result)
      setIsEditingProfile(true);
      // setProfile({ ...profile, profilePic: base64String });
    };
    reader.readAsDataURL(file);
  }
};

  useEffect(() => {
    getProfileData()
  }, [getProfileData])

  useEffect(() => {
    const normalizeAdImageUrl = (value) => {
      const raw = String(value || "").trim();
      if (!raw) return "";
      if (/^https?:\/\//i.test(raw)) return raw;
      return "";
    };
    const loadSponsoredAds = async () => {
      try {
        const res = await fetch(apiUrl("getSponsoredAds"));
        if (!res.ok) throw new Error("failed");
        const rows = await res.json();
        const normalized = Array.isArray(rows)
          ? rows.map((r) => ({
              id: r.id,
              title: r.title || "",
              subText: r.sub_text || "",
              imageUrl: normalizeAdImageUrl(r.image_url),
              ctaText: r.cta_text || "Learn More",
              ctaUrl: r.cta_url || "",
            }))
          : [];
        setSponsoredAds(normalized);
      } catch (_) {
        setSponsoredAds([]);
      }
    };
    loadSponsoredAds();
  }, []);

  useEffect(() => {
    setActiveSponsoredIndex(0);
  }, [sponsoredAds]);

  useEffect(() => {
    if (sponsoredAds.length <= 1) return;
    const intervalId = window.setInterval(() => {
      setActiveSponsoredIndex((prev) => (prev + 1) % sponsoredAds.length);
    }, 10000);
    return () => window.clearInterval(intervalId);
  }, [sponsoredAds]);

  useEffect(() => {
    if (!location.state?.verifyCorporateEmail) return;
    const toastId = "profile-verify-corporate-email";
    if (!toast.isActive(toastId)) {
      toast.info("Verify your corporate email below to unlock creating posts and sending interest.", {
        autoClose: 6000,
        toastId,
      });
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const handleSponsoredAdClick = useCallback((url) => {
    const raw = String(url || "").trim();
    if (!raw) return;
    if (/^https?:\/\//i.test(raw)) {
      window.open(raw, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(raw.startsWith("/") ? raw : `/${raw}`);
  }, [navigate]);

  const rotatingSponsoredAds = sponsoredAds.slice(0, 3);
  const activeSponsoredAd =
    rotatingSponsoredAds.length > 0
      ? rotatingSponsoredAds[activeSponsoredIndex % rotatingSponsoredAds.length]
      : null;

  if (isLoading) {
    return (
      <div className="profile-container">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          fontSize: '18px',
          color: '#021138'
        }}>
          Loading profile data...
        </div>
      </div>
    )
  }

  return (
    <div className="profile-container">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-header-content">
          <div className="profile-picture-section">
            {updatedPic.length > 0 || profile.profilePic ? (
              <img 
                src={updatedPic.length > 0 ? updatedPic : profile.profilePic} 
                alt="Profile" 
                className="profile-picture"
              />
            ) : (
              <div className="profile-picture-placeholder">
                {profile.firstName ? profile.firstName.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
            <label htmlFor="upload-photo" className="change-pic-btn">
              <FaPencilAlt /> Change
            </label>
            <input
              type="file"
              id="upload-photo"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageUpload}
            />
          </div>
          
          <div className="profile-info-section">
            <h1 className="profile-name">
              {profile.firstName} {profile.lastName}
            </h1>
            <p className="profile-title">
              {profile.aboutMe || "Welcome to Corporate NRI Community"}
            </p>
          </div>
        </div>
      </div>

      {/* Profile Content Grid */}
      <div className="profile-content">
        {/* Main Profile Details */}
        <div className="profile-details">
          <div className="section-title">
            <div className="section-title-left">
              <FaUser className="section-icon" />
              <h3>Personal Information</h3>
            </div>
            {!isEditingProfile && (
              <button className="edit-btn" onClick={handleEditProfile}>
                <FaPencilAlt /> Edit Profile
              </button>
            )}
            {isEditingProfile && (
              <div className="edit-action-buttons">
                <button className="save-btn" onClick={handleUpdateAllData}>
                  <FaSave /> Update Data
                </button>
                <button className="cancel-btn" onClick={handleCancelEdit}>
                  <FaTimes /> Cancel
                </button>
              </div>
            )}
          </div>
          
          <div className="profile-fields">
            <ProfileField
              label="About Me"
              value={profile.aboutMe}
              isEditing={editingField === "aboutMe"}
              onChange={(e) => handleChange("aboutMe", e.target.value)}
              icon={<FaUser />}
              fieldType="textarea"
              isGlobalEditMode={isEditingProfile}
            />

            <ProfileField
              label="First Name"
              value={profile.firstName}
              isEditing={editingField === "firstName"}
              onChange={(e) => handleChange("firstName", e.target.value)}
              icon={<FaUser />}
              isGlobalEditMode={isEditingProfile}
            />

            <ProfileField
              label="Last Name"
              value={profile.lastName}
              isEditing={editingField === "lastName"}
              onChange={(e) => handleChange("lastName", e.target.value)}
              icon={<FaUser />}
              isGlobalEditMode={isEditingProfile}
            />

            <ProfileField
              label="Gender"
              value={profile.gender}
              isEditing={editingField === "gender"}
              onChange={(e) => handleChange("gender", e.target.value)}
              icon={<FaVenusMars />}
              isGlobalEditMode={isEditingProfile}
            />

            <ProfileField
              label="Date of Birth"
              value={profile.dob}
              isEditing={editingField === "dob"}
              onChange={(e) => handleChange("dob", e.target.value)}
              icon={<FaCalendarAlt />}
              fieldType="date"
              isGlobalEditMode={isEditingProfile}
            />

            <ProfileField
              label="Email Address"
              value={profile.email}
              isEditing={editingField === "email"}
              onChange={(e) => handleChange("email", e.target.value)}
              icon={<FaEnvelope />}
              fieldType="email"
              isGlobalEditMode={isEditingProfile}
              isEditable={false}
            />

            <ProfileField
              label="Company / College"
              value={profile.companyName}
              isEditing={false}
              onChange={() => {}}
              icon={<FaBriefcase />}
              isGlobalEditMode={isEditingProfile}
              isEditable={false}
            />

            <div
              className={`profile-field ${editingField === "corporateEmail" || isEditingProfile ? "editing" : ""}`}
            >
              <label>
                <FaEnvelope className="field-icon" />
                Corporate Email
                {profile.isCorporateVerified && !corporateEmailChanged && (
                  <span className="corp-verified-pill" title="Corporate email verified">
                    <FaCheckCircle style={{ color: "#16a34a", marginLeft: 8, verticalAlign: "middle" }} />
                    <span style={{ marginLeft: 4, fontSize: 13, color: "#16a34a", fontWeight: 600 }}>
                      Verified
                    </span>
                  </span>
                )}
              </label>
              {editingField === "corporateEmail" || isEditingProfile ? (
                <>
                  {showCorpSplit ? (
                    <>
                      <div className="profile-corp-email-row">
                        <input
                          type="text"
                          className="profile-input-like"
                          value={corpLocalPart}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (/^[a-z0-9._%+-]*$/i.test(v)) {
                              setCorpLocalPart(v);
                            }
                          }}
                          placeholder="yourname"
                        />
                        <span className="profile-corp-email-domain">@{effectiveDomain}</span>
                      </div>
                    </>
                  ) : (
                    <input
                      type="email"
                      className="profile-input-like"
                      value={profile.corporateEmail}
                      onChange={(e) => handleChange("corporateEmail", e.target.value)}
                      placeholder="you@company.com"
                    />
                  )}
                  {isEditingProfile && corporateEmailChanged && (
                    <div className="corp-email-verify-panel">
                      <p className="corp-email-verify-hint">
                        To change your corporate email, we’ll send a one-time code to the new address (same as signup).
                        Save other profile details with <strong>Update Data</strong> anytime; the listed corporate email
                        updates only after you verify.
                      </p>
                      <div className="corp-email-verify-row">
                        <button
                          type="button"
                          className="corp-otp-btn"
                          disabled={corpOtpBusy}
                          onClick={handleSendCorporateOtp}
                        >
                          Send verification code
                        </button>
                      </div>
                      <div className="corp-email-verify-row">
                        <input
                          type="text"
                          inputMode="numeric"
                          className="corp-otp-input"
                          placeholder="Enter code"
                          value={corpOtp}
                          onChange={(e) => setCorpOtp(e.target.value.replace(/\s/g, ""))}
                          disabled={corpOtpBusy}
                        />
                        <button
                          type="button"
                          className="corp-otp-btn primary"
                          disabled={corpOtpBusy}
                          onClick={handleVerifyCorporateOtp}
                        >
                          Verify email
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="profile-corp-email-display">
                  {showCorpSplit ? (
                    <>
                      <div className="profile-corp-email-row profile-corp-email-row--readonly">
                        <span>{corpLocalPart || "—"}</span>
                        <span className="profile-corp-email-domain">@{effectiveDomain}</span>
                      </div>
                    </>
                  ) : (
                    <p style={{ margin: 0 }}>
                      {profile.corporateEmail || "Not provided"}
                      {profile.isCorporateVerified && (
                        <FaCheckCircle style={{ color: "#16a34a", marginLeft: 8, verticalAlign: "middle" }} />
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>

            <ProfileField
              label="Mobile Number"
              value={profile.mobile}
              isEditing={editingField === "mobile"}
              onChange={(e) => handleChange("mobile", e.target.value)}
              icon={<FaPhone />}
              fieldType="mobile"
              isGlobalEditMode={isEditingProfile}
              isEditable={false}
            />

            <div className={`profile-field ${editingField === 'preferredLocation' || isEditingProfile ? 'editing' : ''}`}>
              <label>
                <FaMapMarkerAlt className="field-icon" />
                Preferred Location
              </label>
              {editingField === 'preferredLocation' || isEditingProfile ? (
                <Autocomplete 
                  inputValue={profile.preferredLocation} 
                  setInputValue={(value) => { setProfile({ ...profile, preferredLocation: value }) }}
                />
              ) : (
                <p>{profile.preferredLocation?.trim() || ""}</p>
              )}
            </div>

            <AddressField
              address={profile.address}
              isEditing={editingField === "address"}
              onChange={handleAddressChange}
              isGlobalEditMode={isEditingProfile}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="profile-sidebar">

          {/* Sponsored (same block as dashboard home) */}
          <div className="ad-section profile-sponsored-section">
            <div className="ad-container">
              <div className="ad-label">Sponsored</div>
              <div className="manual-sponsored-list">
                {activeSponsoredAd ? (
                  <button
                    key={activeSponsoredAd.id}
                    type="button"
                    className="manual-sponsored-card"
                    onClick={() => handleSponsoredAdClick(activeSponsoredAd.ctaUrl)}
                  >
                    {activeSponsoredAd.imageUrl ? (
                      <img
                        src={activeSponsoredAd.imageUrl}
                        alt={activeSponsoredAd.title || "Sponsored ad"}
                        className="manual-sponsored-image"
                      />
                    ) : null}
                    <h4>{activeSponsoredAd.title || "Sponsored"}</h4>
                    <p>{activeSponsoredAd.subText || ""}</p>
                    <span>{activeSponsoredAd.ctaText || "Learn More"}</span>
                  </button>
                ) : (
                  <div className="manual-sponsored-empty">No sponsored ads right now.</div>
                )}
              </div>
            </div>
          </div>

          {/* Advertisement */}
          <div className="sidebar-card">
            <VerticalAd />
          </div>
          
          {/* Account Management */}
          <div className="sidebar-card">
            <h4>Account Management</h4>
            <div className="quick-actions">
              <button className="action-btn danger" onClick={() => setIsOpen(true)}>
                <FaTrash />
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isOpen && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h2>Delete Account</h2>
            <p>Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed.</p>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setIsOpen(false)}>
                Cancel
              </button>
              <button className="action-btn danger" onClick={handleDelete}>
                Yes, Delete Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
